import { v4 as uuidv4 } from 'uuid';
import { client } from './amplifyClient';
import { communityGraphqlRequest } from './communityGraphql';
import { createNotification } from './notifications';
import { isMiraPlusHost } from './miraplusAppUrl';
import { listKidProfilesForParentSession } from './parentKidProfiles';
import { listAllKidCareTeamMembers } from './kidCareTeam';
import { listAcceptedReferralsForKid } from './therapistReferral';

/** Synthetic Broadcasts-tab thread for Super Admin → org admin announcements. */
export const PLATFORM_ANNOUNCEMENTS_ID = 'platform-announcements';

export type MessageAudience = 'USER' | 'ORG_THERAPISTS' | 'ORG_PARENTS' | 'ORG_EVERYONE';

export type AppMessage = {
  id: string;
  fromUserId: string;
  toUserId?: string | null;
  organizationId?: string | null;
  kidProfileId?: string | null;
  body: string;
  audience?: MessageAudience | null;
  threadId?: string | null;
  readAt?: string | null;
  createdAt: string;
};

export type MessageRecipientOption = {
  userId: string;
  label: string;
  role?: string | null;
  subtitle?: string | null;
};

export type ComposeAudienceOption = {
  id: MessageAudience | 'PICK_USER';
  label: string;
  description: string;
};

const MESSAGE_SELECTION = `
  id fromUserId toUserId organizationId kidProfileId body audience threadId readAt createdAt
`;

const THERAPIST_ROLES = new Set(['THERAPIST', 'CLINICIAN', 'INTERNS', 'DOCTOR', 'SME']);
const PARENT_ROLES = new Set(['PARENT', 'CAREGIVER']);
const ORG_ADMIN_ROLES = new Set(['ORG_ADMIN']);
const SUPER_ROLES = new Set(['SUPER_ADMIN', 'ADMIN']);

export function isParentOrCaregiverRole(role?: string | null): boolean {
  return PARENT_ROLES.has(normalizeRole(role));
}

/** Org dashboard team inbox: therapists and org admins — not parents. */
export function isOrgTeamMessagingRole(role?: string | null): boolean {
  const r = normalizeRole(role);
  return THERAPIST_ROLES.has(r) || ORG_ADMIN_ROLES.has(r);
}

export function isOrgAdminRole(role?: string | null): boolean {
  return ORG_ADMIN_ROLES.has(normalizeRole(role));
}

export function isSuperAdminRole(role?: string | null): boolean {
  return normalizeRole(role) === 'SUPER_ADMIN';
}

export function isPlatformBroadcastSenderRole(role?: string | null): boolean {
  return isSuperAdminRole(role);
}

/** Brand shown to org admins — never the Super Admin username. */
export function getPlatformAnnouncementBrandName(): string {
  return isMiraPlusHost() ? 'MiraPlus' : 'TinyWins';
}

export function isPlatformAnnouncementMessage(
  message: AppMessage,
  senderRole?: string | null,
): boolean {
  if (message.kidProfileId) return false;
  if (message.organizationId) return false;
  if (parseGroupCreateMeta(message.body)) return false;
  return isPlatformBroadcastSenderRole(senderRole);
}

export function listPlatformAnnouncementMessages(
  myUserId: string,
  messages: AppMessage[],
  senderRoles: Record<string, string>,
): AppMessage[] {
  return messages
    .filter((message) => {
      if (message.toUserId !== myUserId && message.fromUserId !== myUserId) return false;
      return isPlatformAnnouncementMessage(message, senderRoles[message.fromUserId]);
    })
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function buildPlatformAnnouncementPreview(
  myUserId: string,
  messages: AppMessage[],
  senderRoles: Record<string, string>,
): TherapistChatGroupPreview | null {
  const rows = listPlatformAnnouncementMessages(myUserId, messages, senderRoles);
  if (rows.length === 0) return null;
  const last = rows[rows.length - 1];
  return {
    groupId: PLATFORM_ANNOUNCEMENTS_ID,
    name: getPlatformAnnouncementBrandName(),
    memberIds: [],
    createdBy: last.fromUserId,
    kind: 'broadcast',
    lastMessage: last.body,
    lastAt: last.createdAt,
    unreadCount: rows.filter((m) => m.toUserId === myUserId && !m.readAt).length,
  };
}

export async function listPlatformAnnouncementMessagesForUser(
  userId: string,
): Promise<AppMessage[]> {
  const [inbox, sent] = await Promise.all([
    listInboxMessages(userId),
    listSentMessages(userId),
  ]);
  const byId = new Map<string, AppMessage>();
  for (const message of [...inbox, ...sent]) byId.set(message.id, message);
  const allMessages = Array.from(byId.values());
  const senderIds = [...new Set(allMessages.map((m) => m.fromUserId).filter(Boolean))];
  const senderRoles = await resolveUserRolesById(senderIds);
  return listPlatformAnnouncementMessages(userId, allMessages, senderRoles);
}

export type TeamChannelKind = 'group' | 'broadcast';

export function canPostInTeamChannel(params: {
  role?: string | null;
  kind?: TeamChannelKind | null;
}): boolean {
  if ((params.kind || 'group') === 'broadcast') {
    return isOrgAdminRole(params.role);
  }
  return isOrgTeamMessagingRole(params.role);
}

export async function resolveUserRolesById(userIds: string[]): Promise<Record<string, string>> {
  const roles: Record<string, string> = {};
  await Promise.all(
    userIds.map(async (id) => {
      try {
        const resp = await client.models.User.get({ id }, { authMode: 'apiKey' });
        roles[id] = normalizeRole(resp.data?.role);
      } catch {
        roles[id] = '';
      }
    }),
  );
  return roles;
}

export function filterMessagesForOrgTeamHub(
  messages: AppMessage[],
  myUserId: string,
  peerRoles: Record<string, string>,
): AppMessage[] {
  const groupIds = groupThreadIdsFromMessages(messages);
  return messages.filter((message) => {
    // Parent ↔ therapist threads always carry a kidProfileId; team DMs / groups do not.
    if (message.kidProfileId) return false;
    if (message.threadId && groupIds.has(message.threadId)) return false;
    const peer = message.fromUserId === myUserId ? message.toUserId : message.fromUserId;
    if (!peer) return false;
    const role = peerRoles[peer];
    if (!role || isParentOrCaregiverRole(role)) return false;
    // Team DMs only. Super Admin announcements are filed under Broadcasts.
    return isOrgTeamMessagingRole(role);
  });
}

/** Encoded system body when a therapist creates a custom chat group. */
export const TW_GROUP_CREATE_PREFIX = '::tw_group_create::';

export type TherapistChatGroupMeta = {
  name: string;
  memberIds: string[];
  createdBy: string;
  /** Default group is two-way. Broadcast is org-admin-only posting. */
  kind?: TeamChannelKind;
  /** Soft-deleted by creator — hidden from all members' group lists. */
  deleted?: boolean;
};

export type TherapistChatGroupPreview = {
  groupId: string;
  name: string;
  memberIds: string[];
  createdBy: string;
  kind: TeamChannelKind;
  lastMessage: string | null;
  lastAt: string | null;
  unreadCount: number;
};

export type OrgTeamConversationPreview = {
  peerUserId: string;
  peerLabel: string;
  peerRole?: string | null;
  lastMessage: string | null;
  lastAt: string | null;
  unreadCount: number;
  hasSent: boolean;
  hasReceived: boolean;
};

export function encodeGroupCreateBody(meta: TherapistChatGroupMeta): string {
  return `${TW_GROUP_CREATE_PREFIX}${JSON.stringify(meta)}`;
}

export function parseGroupCreateMeta(body: string): TherapistChatGroupMeta | null {
  if (!body.startsWith(TW_GROUP_CREATE_PREFIX)) return null;
  try {
    const raw = JSON.parse(body.slice(TW_GROUP_CREATE_PREFIX.length)) as TherapistChatGroupMeta;
    if (!raw?.name || !Array.isArray(raw.memberIds) || !raw.createdBy) return null;
    return {
      name: String(raw.name).trim(),
      memberIds: raw.memberIds.map(String).filter(Boolean),
      createdBy: String(raw.createdBy),
      kind: raw.kind === 'broadcast' ? 'broadcast' : 'group',
      deleted: Boolean(raw.deleted),
    };
  } catch {
    return null;
  }
}

export function isTherapistGroupMessage(message: AppMessage): boolean {
  if (message.kidProfileId) return false;
  if (parseGroupCreateMeta(message.body)) return true;
  // Non-create group messages share a threadId that was opened via create meta;
  // callers that already know groupThreadIds should filter those separately.
  return false;
}

export function displayTeamMessageBody(body: string): string {
  const meta = parseGroupCreateMeta(body);
  if (!meta) return body;
  if (meta.kind === 'broadcast') return `${meta.name} broadcast created`;
  return `${meta.name} group created`;
}

export function channelKindFromMeta(meta?: TherapistChatGroupMeta | null): TeamChannelKind {
  return meta?.kind === 'broadcast' ? 'broadcast' : 'group';
}

/** Collect group threads from inbox/sent (no kid). */
export function listTherapistChatGroupsFromMessages(
  myUserId: string,
  messages: AppMessage[],
): TherapistChatGroupPreview[] {
  const team = messages.filter((m) => !m.kidProfileId);
  const byThread = new Map<string, AppMessage[]>();
  for (const m of team) {
    if (!m.threadId) continue;
    const rows = byThread.get(m.threadId) || [];
    rows.push(m);
    byThread.set(m.threadId, rows);
  }

  const groups: TherapistChatGroupPreview[] = [];
  for (const [groupId, rows] of byThread) {
    const create = rows.map((r) => parseGroupCreateMeta(r.body)).find(Boolean) || null;
    if (!create || create.deleted) continue;
    if (!create.memberIds.includes(myUserId)) continue;
    const sorted = [...rows].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const last = sorted[sorted.length - 1];
    groups.push({
      groupId,
      name: create.name,
      memberIds: create.memberIds,
      createdBy: create.createdBy,
      kind: channelKindFromMeta(create),
      lastMessage: last ? displayTeamMessageBody(last.body) : null,
      lastAt: last?.createdAt || null,
      unreadCount: sorted.filter(
        (m) => m.toUserId === myUserId && !m.readAt && !parseGroupCreateMeta(m.body),
      ).length,
    });
  }

  return groups.sort((a, b) => {
    const at = a.lastAt || '';
    const bt = b.lastAt || '';
    if (at && bt) return bt.localeCompare(at);
    if (at) return -1;
    if (bt) return 1;
    return a.name.localeCompare(b.name);
  });
}

export function groupThreadIdsFromMessages(messages: AppMessage[]): Set<string> {
  const ids = new Set<string>();
  for (const m of messages) {
    if (m.kidProfileId || !m.threadId) continue;
    if (parseGroupCreateMeta(m.body)) ids.add(m.threadId);
  }
  return ids;
}

export async function createTherapistChatGroup(params: {
  fromUserId: string;
  fromRole?: string | null;
  organizationId: string;
  name: string;
  memberUserIds: string[];
  kind?: TeamChannelKind;
}): Promise<{ groupId: string; name: string; memberIds: string[]; kind: TeamChannelKind }> {
  const role = normalizeRole(params.fromRole);
  const kind: TeamChannelKind = params.kind === 'broadcast' ? 'broadcast' : 'group';
  if (kind === 'broadcast' && !isOrgAdminRole(role)) {
    throw new Error('Only org admins can create a broadcast channel.');
  }
  if (!isOrgTeamMessagingRole(role)) {
    throw new Error('Only therapists and org staff can create groups.');
  }
  if (!params.organizationId) throw new Error('Organization is required.');
  const name = params.name.trim();
  if (name.length < 2) {
    throw new Error(
      kind === 'broadcast'
        ? 'Broadcast name must be at least 2 characters.'
        : 'Group name must be at least 2 characters.',
    );
  }

  const memberIds = Array.from(
    new Set([params.fromUserId, ...params.memberUserIds.map(String).filter(Boolean)]),
  );
  if (memberIds.length < 2) {
    throw new Error(
      kind === 'broadcast'
        ? 'Add at least one teammate to the broadcast channel.'
        : 'Add at least one teammate to the group.',
    );
  }

  const contacts = await listUsersByOrg(params.organizationId);
  const allowed = new Set(
    contacts.filter((u) => u.id && isOrgTeamMessagingRole(u.role)).map((u) => u.id as string),
  );
  for (const id of memberIds) {
    if (!allowed.has(id)) {
      throw new Error('Every member must be a therapist or org staff in your organization.');
    }
  }

  const groupId = uuidv4();
  const createdAt = new Date().toISOString();
  const meta: TherapistChatGroupMeta = {
    name,
    memberIds,
    createdBy: params.fromUserId,
    kind,
  };
  const body = encodeGroupCreateBody(meta);

  for (const toUserId of memberIds) {
    await createMessageGraphql({
      id: uuidv4(),
      fromUserId: params.fromUserId,
      toUserId,
      organizationId: params.organizationId,
      body,
      audience: 'USER',
      threadId: groupId,
      createdAt,
    });
  }

  // Notify other members
  for (const toUserId of memberIds) {
    if (toUserId === params.fromUserId) continue;
    try {
      const senderName = await resolveUserLabel(params.fromUserId);
      await createNotification({
        toUserId,
        fromUserId: params.fromUserId,
        organizationId: params.organizationId,
        notificationType: 'MESSAGE_RECEIVED',
        title: `${senderName} added you to “${name}”`,
        body:
          kind === 'broadcast'
            ? 'Open Messages to read org-admin announcements. Only org admins can post.'
            : 'Open Messages to chat with your team group.',
        payload: {
          kind: 'USER_MESSAGE',
          orgTeam: true,
          groupId,
          fromUserId: params.fromUserId,
        },
        actionUrl: `/org-admin-dashboard?section=messages&group=${encodeURIComponent(groupId)}`,
      });
    } catch {
      /* non-blocking */
    }
  }

  return { groupId, name, memberIds, kind };
}

/** Creator updates group name and/or members (updates all create-meta copies). */
export async function updateTherapistChatGroup(params: {
  fromUserId: string;
  fromRole?: string | null;
  organizationId: string;
  groupId: string;
  name: string;
  memberUserIds: string[];
}): Promise<TherapistChatGroupMeta> {
  const role = normalizeRole(params.fromRole);
  if (!isOrgTeamMessagingRole(role)) {
    throw new Error('Only therapists and org staff can edit groups.');
  }
  if (!params.organizationId) throw new Error('Organization is required.');
  const name = params.name.trim();
  if (name.length < 2) throw new Error('Name must be at least 2 characters.');

  const thread = await listGroupThreadMessages(params.fromUserId, params.groupId);
  const createMsgs = thread.filter((m) => parseGroupCreateMeta(m.body));
  const existing = createMsgs.map((m) => parseGroupCreateMeta(m.body)).find(Boolean);
  if (!existing || existing.deleted) throw new Error('Group not found.');
  const existingKind = channelKindFromMeta(existing);
  if (existingKind === 'broadcast' && !isOrgAdminRole(role)) {
    throw new Error('Only org admins can edit a broadcast channel.');
  }
  if (existing.createdBy !== params.fromUserId) {
    throw new Error(
      existingKind === 'broadcast'
        ? 'Only the channel creator can edit this broadcast.'
        : 'Only the group creator can edit this group.',
    );
  }

  const memberIds = Array.from(
    new Set([params.fromUserId, ...params.memberUserIds.map(String).filter(Boolean)]),
  );
  if (memberIds.length < 2) {
    throw new Error('Add at least one teammate to the group.');
  }

  const contacts = await listUsersByOrg(params.organizationId);
  const allowed = new Set(
    contacts.filter((u) => u.id && isOrgTeamMessagingRole(u.role)).map((u) => u.id as string),
  );
  for (const id of memberIds) {
    if (!allowed.has(id)) {
      throw new Error('Every member must be a therapist or org staff in your organization.');
    }
  }

  const meta: TherapistChatGroupMeta = {
    name,
    memberIds,
    createdBy: existing.createdBy,
    kind: existingKind,
  };
  const body = encodeGroupCreateBody(meta);

  for (const msg of createMsgs) {
    await updateMessageBodyGraphql(msg.id, body);
  }

  const alreadyHaveCopy = new Set(createMsgs.map((m) => m.toUserId));
  const createdAt = new Date().toISOString();
  for (const toUserId of memberIds) {
    if (alreadyHaveCopy.has(toUserId)) continue;
    await createMessageGraphql({
      id: uuidv4(),
      fromUserId: params.fromUserId,
      toUserId,
      organizationId: params.organizationId,
      body,
      audience: 'USER',
      threadId: params.groupId,
      createdAt,
    });
    try {
      const senderName = await resolveUserLabel(params.fromUserId);
      await createNotification({
        toUserId,
        fromUserId: params.fromUserId,
        organizationId: params.organizationId,
        notificationType: 'MESSAGE_RECEIVED',
        title: `${senderName} added you to “${name}”`,
        body:
          existingKind === 'broadcast'
            ? 'Open Messages to read org-admin announcements. Only org admins can post.'
            : 'Open Messages to chat with your team group.',
        payload: {
          kind: 'USER_MESSAGE',
          orgTeam: true,
          groupId: params.groupId,
          fromUserId: params.fromUserId,
        },
        actionUrl: `/org-admin-dashboard?section=messages&group=${encodeURIComponent(params.groupId)}`,
      });
    } catch {
      /* non-blocking */
    }
  }

  return meta;
}

/** Creator soft-deletes the group (hidden for everyone). */
export async function deleteTherapistChatGroup(params: {
  fromUserId: string;
  fromRole?: string | null;
  groupId: string;
}): Promise<void> {
  const role = normalizeRole(params.fromRole);
  if (!isOrgTeamMessagingRole(role)) {
    throw new Error('Only therapists and org staff can delete groups.');
  }

  const thread = await listGroupThreadMessages(params.fromUserId, params.groupId);
  const createMsgs = thread.filter((m) => parseGroupCreateMeta(m.body));
  const existing = createMsgs.map((m) => parseGroupCreateMeta(m.body)).find(Boolean);
  if (!existing) throw new Error('Group not found.');
  const existingKind = channelKindFromMeta(existing);
  if (existingKind === 'broadcast' && !isOrgAdminRole(role)) {
    throw new Error('Only org admins can delete a broadcast channel.');
  }
  if (existing.createdBy !== params.fromUserId) {
    throw new Error(
      existingKind === 'broadcast'
        ? 'Only the channel creator can delete this broadcast.'
        : 'Only the group creator can delete this group.',
    );
  }

  const body = encodeGroupCreateBody({ ...existing, deleted: true });
  for (const msg of createMsgs) {
    await updateMessageBodyGraphql(msg.id, body);
  }
}

export async function listGroupThreadMessages(
  userId: string,
  groupId: string,
): Promise<AppMessage[]> {
  const [inbox, sent] = await Promise.all([listInboxMessages(userId), listSentMessages(userId)]);
  const byId = new Map<string, AppMessage>();
  for (const m of [...inbox, ...sent]) {
    if (m.kidProfileId) continue;
    if (m.threadId !== groupId) continue;
    byId.set(m.id, m);
  }
  return Array.from(byId.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function sendTherapistGroupMessage(params: {
  fromUserId: string;
  fromRole?: string | null;
  organizationId: string;
  groupId: string;
  body: string;
}): Promise<{ created: number }> {
  const role = normalizeRole(params.fromRole);
  if (!isOrgTeamMessagingRole(role)) {
    throw new Error('Only therapists and org staff can send group messages.');
  }
  const text = params.body.trim();
  if (!text) throw new Error('Message cannot be empty.');
  if (!params.organizationId) throw new Error('Organization is required.');

  const thread = await listGroupThreadMessages(params.fromUserId, params.groupId);
  const create = thread.map((m) => parseGroupCreateMeta(m.body)).find(Boolean);
  if (!create || create.deleted) throw new Error('Group not found.');
  if (!create.memberIds.includes(params.fromUserId)) {
    throw new Error('You are not a member of this group.');
  }
  const channelKind = channelKindFromMeta(create);
  if (!canPostInTeamChannel({ role, kind: channelKind })) {
    throw new Error('Only org admins can send messages in a broadcast channel.');
  }

  const recipients = create.memberIds.filter((id) => id !== params.fromUserId);
  if (recipients.length === 0) throw new Error('No other members in this group.');

  const createdAt = new Date().toISOString();
  let created = 0;
  for (const toUserId of recipients) {
    const id = uuidv4();
    await createMessageGraphql({
      id,
      fromUserId: params.fromUserId,
      toUserId,
      organizationId: params.organizationId,
      body: text,
      audience: 'USER',
      threadId: params.groupId,
      createdAt,
    });
    created += 1;
    try {
      const senderName = await resolveUserLabel(params.fromUserId);
      const preview = text.length > 100 ? `${text.slice(0, 97)}…` : text;
      await createNotification({
        toUserId,
        fromUserId: params.fromUserId,
        organizationId: params.organizationId,
        notificationType: 'MESSAGE_RECEIVED',
        title:
          channelKind === 'broadcast'
            ? `${create.name}: announcement from ${senderName}`
            : `${create.name}: ${senderName}`,
        body: preview,
        payload: {
          kind: 'USER_MESSAGE',
          orgTeam: true,
          groupId: params.groupId,
          fromUserId: params.fromUserId,
        },
        actionUrl: `/org-admin-dashboard?section=messages&group=${encodeURIComponent(params.groupId)}`,
      });
    } catch {
      /* non-blocking */
    }
  }
  return { created };
}

export function subscribeGroupMessagesLive(params: {
  userId: string;
  groupId: string;
  onMessages: (messages: AppMessage[]) => void;
  intervalMs?: number;
}): () => void {
  let stopped = false;
  let lastFp = '';
  const intervalMs = params.intervalMs ?? 2000;

  const pull = async () => {
    if (stopped) return;
    try {
      const rows = await listGroupThreadMessages(params.userId, params.groupId);
      if (stopped) return;
      const fp = messagesFingerprint(rows);
      if (fp !== lastFp) {
        lastFp = fp;
        params.onMessages(rows);
      }
    } catch {
      /* keep polling */
    }
  };

  void pull();
  const timer = window.setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void pull();
  }, intervalMs);

  const onFocus = () => {
    void pull();
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onFocus);

  return () => {
    stopped = true;
    window.clearInterval(timer);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onFocus);
  };
}

/** Silent poll of whole team inbox (direct + groups) so new groups appear without refresh. */
export function subscribeOrgTeamInboxLive(params: {
  userId: string;
  organizationId: string;
  onUpdate: (data: {
    contacts: MessageRecipientOption[];
    conversations: OrgTeamConversationPreview[];
    groups: TherapistChatGroupPreview[];
    platformAnnouncement: TherapistChatGroupPreview | null;
  }) => void;
  intervalMs?: number;
}): () => void {
  let stopped = false;
  let lastFp = '';
  const intervalMs = params.intervalMs ?? 2500;

  const pull = async () => {
    if (stopped || !params.userId || !params.organizationId) return;
    try {
      const [inbox, sent, teamContacts] = await Promise.all([
        listInboxMessages(params.userId),
        listSentMessages(params.userId),
        listOrgTeamMessagingContacts({
          organizationId: params.organizationId,
          excludeUserId: params.userId,
        }),
      ]);
      if (stopped) return;
      const byId = new Map<string, AppMessage>();
      for (const message of [...inbox, ...sent]) byId.set(message.id, message);
      const allMessages = Array.from(byId.values());
      const fp = messagesFingerprint(allMessages);
      if (fp === lastFp) return;
      lastFp = fp;

      const allPeerIds = new Set<string>();
      for (const message of allMessages) {
        const peer =
          message.fromUserId === params.userId ? message.toUserId : message.fromUserId;
        if (peer) allPeerIds.add(peer);
      }
      const peerRoles = await resolveUserRolesById([...allPeerIds]);
      if (stopped) return;
      const teamMessages = filterMessagesForOrgTeamHub(allMessages, params.userId, peerRoles);
      params.onUpdate({
        contacts: teamContacts,
        conversations: buildOrgTeamConversationPreviews({
          myUserId: params.userId,
          messages: teamMessages,
          contacts: teamContacts,
        }),
        groups: listTherapistChatGroupsFromMessages(params.userId, allMessages),
        platformAnnouncement: buildPlatformAnnouncementPreview(
          params.userId,
          allMessages,
          peerRoles,
        ),
      });
    } catch {
      /* keep polling */
    }
  };

  void pull();
  const timer = window.setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void pull();
  }, intervalMs);
  const onFocus = () => {
    void pull();
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onFocus);

  return () => {
    stopped = true;
    window.clearInterval(timer);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onFocus);
  };
}

/** WhatsApp-style ticks for outbound messages. */
export type MessageTickStatus = 'pending' | 'sent' | 'delivered' | 'read';

export type DisplayThreadMessage = AppMessage & {
  /** Fan-out sibling copies (group sends) — used for aggregate read ticks. */
  _siblings?: AppMessage[];
};

/**
 * Collapse group fan-out copies into one bubble per logical send.
 * Incoming messages are unique per viewer; outbound group sends create N copies.
 */
export function collapseThreadMessagesForDisplay(
  messages: AppMessage[],
  myUserId: string,
): DisplayThreadMessage[] {
  const sorted = [...messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const outboundBuckets = new Map<string, AppMessage[]>();

  for (const m of sorted) {
    if (parseGroupCreateMeta(m.body) || m.fromUserId !== myUserId) continue;
    const bucketKey = `${m.threadId || 'dm'}|${m.body}|${m.createdAt.slice(0, 19)}`;
    const bucket = outboundBuckets.get(bucketKey) || [];
    bucket.push(m);
    outboundBuckets.set(bucketKey, bucket);
  }

  const clean: DisplayThreadMessage[] = [];
  const seenOutbound = new Set<string>();
  for (const m of sorted) {
    if (parseGroupCreateMeta(m.body)) {
      clean.push(m);
      continue;
    }
    if (m.fromUserId !== myUserId) {
      clean.push(m);
      continue;
    }
    const bucketKey = `${m.threadId || 'dm'}|${m.body}|${m.createdAt.slice(0, 19)}`;
    if (seenOutbound.has(bucketKey)) continue;
    seenOutbound.add(bucketKey);
    const siblings = outboundBuckets.get(bucketKey) || [m];
    clean.push({
      ...siblings[0],
      _siblings: siblings,
      readAt: siblings.every((s) => Boolean(s.readAt))
        ? siblings.map((s) => s.readAt!).sort().slice(-1)[0]
        : null,
    });
  }
  return clean;
}

export function getOutboundTickStatus(message: DisplayThreadMessage): MessageTickStatus {
  if (message.id.startsWith('local-')) return 'pending';

  const copies =
    message._siblings && message._siblings.length > 0 ? message._siblings : [message];

  // Group fan-out: once saved, always double-grey (delivered) until every copy is read.
  // 2-person groups only create 1 outbound copy — still count as delivered, not single-tick.
  if (message.threadId) {
    if (copies.every((s) => Boolean(s.readAt))) return 'read';
    return 'delivered';
  }

  if (message.readAt) return 'read';
  // Direct message: single tick until the peer opens the chat (blue)
  return 'sent';
}

/** Therapists / clinicians / org admins in the same organization (for team DMs). */
export async function listOrgTeamMessagingContacts(params: {
  organizationId: string;
  excludeUserId: string;
}): Promise<MessageRecipientOption[]> {
  if (!params.organizationId) return [];
  const users = await listUsersByOrg(params.organizationId);
  const out: MessageRecipientOption[] = [];
  for (const u of users) {
    if (!u.id || u.id === params.excludeUserId) continue;
    const role = normalizeRole(u.role);
    if (!isOrgTeamMessagingRole(role)) continue;
    out.push({
      userId: u.id,
      label: userLabel(u),
      role: u.role,
      subtitle: role === 'ORG_ADMIN' ? 'Org admin' : 'Therapist',
    });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

/** Peer thread for org-team DMs (messages with no kidProfileId). */
export async function listPeerThreadMessages(
  userId: string,
  peerUserId: string,
): Promise<AppMessage[]> {
  const [inbox, sent] = await Promise.all([listInboxMessages(userId), listSentMessages(userId)]);
  const all = [...inbox, ...sent];
  const groupIds = groupThreadIdsFromMessages(all);
  const byId = new Map<string, AppMessage>();
  for (const m of all) {
    if (m.kidProfileId) continue;
    if (m.threadId && groupIds.has(m.threadId)) continue;
    const isPeerThread =
      (m.fromUserId === userId && m.toUserId === peerUserId) ||
      (m.fromUserId === peerUserId && m.toUserId === userId);
    if (!isPeerThread) continue;
    byId.set(m.id, m);
  }
  return Array.from(byId.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function subscribePeerMessagesLive(params: {
  userId: string;
  peerUserId: string;
  onMessages: (messages: AppMessage[]) => void;
  intervalMs?: number;
}): () => void {
  let stopped = false;
  let lastFp = '';
  const intervalMs = params.intervalMs ?? 2000;

  const pull = async () => {
    if (stopped) return;
    try {
      const rows = await listPeerThreadMessages(params.userId, params.peerUserId);
      if (stopped) return;
      const fp = messagesFingerprint(rows);
      if (fp !== lastFp) {
        lastFp = fp;
        params.onMessages(rows);
      }
    } catch {
      /* keep polling */
    }
  };

  void pull();
  const timer = window.setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void pull();
  }, intervalMs);

  const onFocus = () => {
    void pull();
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onFocus);

  return () => {
    stopped = true;
    window.clearInterval(timer);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onFocus);
  };
}

/** Group team DMs by peer (no kid). Includes contacts with no messages yet. */
export function buildOrgTeamConversationPreviews(params: {
  myUserId: string;
  messages: AppMessage[];
  contacts: MessageRecipientOption[];
}): OrgTeamConversationPreview[] {
  const contactById = new Map(params.contacts.map((c) => [c.userId, c]));
  const peerIds = new Set<string>(params.contacts.map((c) => c.userId));

  for (const m of params.messages) {
    if (m.kidProfileId) continue;
    const peer = m.fromUserId === params.myUserId ? m.toUserId : m.fromUserId;
    if (peer) peerIds.add(peer);
  }

  const previews: OrgTeamConversationPreview[] = [];
  for (const peerUserId of peerIds) {
    const thread = params.messages
      .filter(
        (m) =>
          !m.kidProfileId &&
          ((m.fromUserId === params.myUserId && m.toUserId === peerUserId) ||
            (m.fromUserId === peerUserId && m.toUserId === params.myUserId)),
      )
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const last = thread.length ? thread[thread.length - 1] : null;
    const contact = contactById.get(peerUserId);
    previews.push({
      peerUserId,
      peerLabel: contact?.label || 'Team member',
      peerRole: contact?.role || contact?.subtitle || null,
      lastMessage: last?.body || null,
      lastAt: last?.createdAt || null,
      unreadCount: thread.filter((m) => m.toUserId === params.myUserId && !m.readAt).length,
      hasSent: thread.some((m) => m.fromUserId === params.myUserId),
      hasReceived: thread.some((m) => m.toUserId === params.myUserId),
    });
  }

  return previews.sort((a, b) => {
    const at = a.lastAt || '';
    const bt = b.lastAt || '';
    if (at && bt) return bt.localeCompare(at);
    if (at) return -1;
    if (bt) return 1;
    return a.peerLabel.localeCompare(b.peerLabel);
  });
}

function normalizeRole(role?: string | null): string {
  return (role || '').toUpperCase().trim();
}

function mapMessage(row: Partial<AppMessage> | null | undefined): AppMessage | null {
  if (!row?.id || !row.fromUserId || !row.body || !row.createdAt) return null;
  return {
    id: row.id,
    fromUserId: row.fromUserId,
    toUserId: row.toUserId ?? null,
    organizationId: row.organizationId ?? null,
    kidProfileId: row.kidProfileId ?? null,
    body: row.body,
    audience: (row.audience as MessageAudience) || 'USER',
    threadId: row.threadId ?? null,
    readAt: row.readAt ?? null,
    createdAt: row.createdAt,
  };
}

function explainMissing(e: unknown): Error {
  const msg = e instanceof Error ? e.message : String(e);
  if (/VITE_COMMUNITY_GRAPHQL_URL|Community messaging API is not configured/i.test(msg)) {
    return e instanceof Error ? e : new Error(msg);
  }
  if (/Unknown type|Message|Cannot query field|not present/i.test(msg)) {
    return new Error(
      'Message model is not available on the community API yet. Confirm tinywins-community deployed Message and VITE_COMMUNITY_GRAPHQL_URL is set.',
    );
  }
  return e instanceof Error ? e : new Error(msg);
}

/** Message CRUD → tinywins-community AppSync (Cognito). Recipient/User lookups stay on TinyWins. */
async function listMessagesGraphql(filter: Record<string, unknown>): Promise<AppMessage[]> {
  const out: AppMessage[] = [];
  let nextToken: string | null = null;
  do {
    const data = await communityGraphqlRequest<{
      listMessages?: {
        items?: Array<Partial<AppMessage> | null> | null;
        nextToken?: string | null;
      };
    }>(
      `query ListMessages($filter: ModelMessageFilterInput, $limit: Int, $nextToken: String) {
        listMessages(filter: $filter, limit: $limit, nextToken: $nextToken) {
          items { ${MESSAGE_SELECTION} }
          nextToken
        }
      }`,
      { filter, limit: 100, nextToken },
    );
    for (const item of data.listMessages?.items || []) {
      const mapped = mapMessage(item);
      if (mapped) out.push(mapped);
    }
    nextToken = data.listMessages?.nextToken ?? null;
  } while (nextToken);
  return out;
}

async function createMessageGraphql(input: Record<string, unknown>): Promise<AppMessage> {
  // Community Message.createdAt is server-managed (read-only); do not send it.
  const { createdAt: _ignored, ...communityInput } = input;
  const data = await communityGraphqlRequest<{
    createMessage?: Partial<AppMessage> | null;
  }>(
    `mutation CreateMessage($input: CreateMessageInput!) {
      createMessage(input: $input) { ${MESSAGE_SELECTION} }
    }`,
    { input: communityInput },
  );
  const mapped = mapMessage(data.createMessage);
  if (!mapped) throw new Error('Message was not created.');
  return mapped;
}

async function updateMessageGraphql(id: string, readAt: string): Promise<void> {
  await communityGraphqlRequest(
    `mutation UpdateMessage($input: UpdateMessageInput!) {
      updateMessage(input: $input) { id readAt }
    }`,
    { input: { id, readAt } },
  );
}

async function updateMessageBodyGraphql(id: string, body: string): Promise<void> {
  await communityGraphqlRequest(
    `mutation UpdateMessage($input: UpdateMessageInput!) {
      updateMessage(input: $input) { id body }
    }`,
    { input: { id, body } },
  );
}

async function listUsersByOrg(organizationId: string): Promise<
  Array<{ id: string; role?: string | null; fName?: string | null; lName?: string | null; email?: string | null }>
> {
  const out: Array<{
    id: string;
    role?: string | null;
    fName?: string | null;
    lName?: string | null;
    email?: string | null;
  }> = [];
  let nextToken: string | undefined;
  do {
    const resp = await client.models.User.list({
      filter: { organizationId: { eq: organizationId } },
      limit: 200,
      nextToken,
      authMode: 'apiKey',
    } as any);
    for (const row of resp.data || []) {
      if (row?.id) out.push(row as any);
    }
    nextToken = resp.nextToken ?? undefined;
  } while (nextToken);
  return out;
}

function userLabel(u: {
  fName?: string | null;
  lName?: string | null;
  email?: string | null;
}): string {
  return `${u.fName || ''} ${u.lName || ''}`.trim() || u.email || 'User';
}

export function composeOptionsForRole(role?: string | null): ComposeAudienceOption[] {
  const r = normalizeRole(role);
  if (PARENT_ROLES.has(r)) {
    return [
      {
        id: 'PICK_USER',
        label: 'Therapist',
        description: 'Message a therapist on your child’s care team',
      },
    ];
  }
  if (THERAPIST_ROLES.has(r)) {
    return [
      {
        id: 'PICK_USER',
        label: 'Parent',
        description: 'Message a parent of a child you work with',
      },
    ];
  }
  // Org admin / super admin messaging disabled for now
  // if (ORG_ADMIN_ROLES.has(r)) {
  //   return [
  //     { id: 'PICK_USER', label: 'One person', description: 'Message one therapist or parent in your org' },
  //     { id: 'ORG_THERAPISTS', label: 'All therapists', description: 'Broadcast to therapists in your org' },
  //     { id: 'ORG_PARENTS', label: 'All parents', description: 'Broadcast to parents in your org' },
  //     { id: 'ORG_EVERYONE', label: 'Everyone', description: 'Broadcast to everyone in your org' },
  //   ];
  // }
  // if (SUPER_ROLES.has(r)) {
  //   return [
  //     { id: 'PICK_USER', label: 'Org admin', description: 'Message an organization admin' },
  //     { id: 'PICK_USER', label: 'Parent', description: 'Message a parent' },
  //     { id: 'PICK_USER', label: 'Therapist', description: 'Message a therapist' },
  //   ];
  // }
  return [];
}

/** Distinct compose modes for UI (super admin has multiple PICK_USER flavors). */
export type SuperPickKind = 'ORG_ADMIN' | 'PARENT' | 'THERAPIST';

export function superAdminPickKinds(): Array<{ kind: SuperPickKind; label: string }> {
  return [
    { kind: 'ORG_ADMIN', label: 'Org admin' },
    { kind: 'PARENT', label: 'Parent' },
    { kind: 'THERAPIST', label: 'Therapist' },
  ];
}

/** Fast kid contacts for chat list — Team + primary therapist only (no org lookups). */
export async function listKidChatContacts(params: {
  senderUserId: string;
  senderRole?: string | null;
  kidProfileId: string;
}): Promise<MessageRecipientOption[]> {
  const role = normalizeRole(params.senderRole);
  // Org admin / super admin messaging disabled
  if (ORG_ADMIN_ROLES.has(role) || SUPER_ROLES.has(role)) return [];
  const kidId = params.kidProfileId;
  if (!kidId) return [];

  const byId = new Map<string, MessageRecipientOption>();
  const push = (opt: MessageRecipientOption) => {
    if (!opt.userId || opt.userId === params.senderUserId) return;
    if (!byId.has(opt.userId)) byId.set(opt.userId, opt);
  };

  try {
    const [kidResp, teams] = await Promise.all([
      client.models.KidProfile.get({ id: kidId }, { authMode: 'apiKey' }),
      client.models.Team.list({
        filter: { kidProfileId: { eq: kidId } },
        limit: 20,
        authMode: 'apiKey',
      } as any),
    ]);
    const kid = kidResp.data;
    const kidName = (kid?.name || '').trim() || 'this child';
    const teamRows = (teams.data || []) as Array<{
      parentId?: string | null;
      adminId?: string | null;
      therapistId?: string | null;
      therapist2Id?: string | null;
    }>;

    const needIds = new Set<string>();
    if (PARENT_ROLES.has(role) /* || ORG_ADMIN_ROLES.has(role) */) {
      if (kid?.primaryTherapistId) needIds.add(kid.primaryTherapistId);
      for (const t of teamRows) {
        if (t.therapistId) needIds.add(t.therapistId);
        if (t.therapist2Id) needIds.add(t.therapist2Id);
      }
    }
    if (THERAPIST_ROLES.has(role) /* || ORG_ADMIN_ROLES.has(role) */) {
      if (kid?.parentId) needIds.add(kid.parentId);
      for (const t of teamRows) {
        const p = t.parentId || t.adminId;
        if (p) needIds.add(p);
      }
    }

    // Also pull accepted-referral therapist ids (one GraphQL list, no per-user org fetch)
    if (PARENT_ROLES.has(role) /* || ORG_ADMIN_ROLES.has(role) */) {
      try {
        const accepted = await listAcceptedReferralsForKid(kidId);
        for (const row of accepted) {
          if (row.therapistId) needIds.add(row.therapistId);
          if (row.therapist2Id) needIds.add(row.therapist2Id);
        }
      } catch {
        /* optional */
      }
    }

    needIds.delete(params.senderUserId);

    const users = await Promise.all(
      Array.from(needIds).map(async (id) => {
        try {
          const resp = await client.models.User.get({ id }, { authMode: 'apiKey' });
          return resp.data;
        } catch {
          return null;
        }
      }),
    );

    for (const u of users) {
      if (!u?.id) continue;
      const ur = normalizeRole(u.role);
      const isTherapist = THERAPIST_ROLES.has(ur);
      const isParent = PARENT_ROLES.has(ur);
      if (PARENT_ROLES.has(role) && !isTherapist) continue;
      if (THERAPIST_ROLES.has(role) && !isParent) continue;
      push({
        userId: u.id,
        label: userLabel(u),
        role: u.role,
        subtitle: isParent ? `${kidName} · Parent` : kidName,
      });
    }
  } catch (e) {
    console.warn('listKidChatContacts', e);
  }

  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export async function listRecipientOptionsForSender(params: {
  senderUserId: string;
  senderRole?: string | null;
  organizationId?: string | null;
  superPickKind?: SuperPickKind | null;
  /** When set, only people linked to this child (parent ↔ therapists). */
  kidProfileId?: string | null;
}): Promise<MessageRecipientOption[]> {
  const role = normalizeRole(params.senderRole);
  const byId = new Map<string, MessageRecipientOption>();

  const push = (opt: MessageRecipientOption) => {
    if (!opt.userId || opt.userId === params.senderUserId) return;
    if (!byId.has(opt.userId)) byId.set(opt.userId, opt);
  };

  try {
    // Kid-scoped care-team messaging (parent / therapist / org admin in client workspace)
    if (params.kidProfileId) {
      const kidId = params.kidProfileId;
      const kidResp = await client.models.KidProfile.get({ id: kidId }, { authMode: 'apiKey' });
      const kid = kidResp.data;
      const kidName = (kid?.name || '').trim() || 'this child';

      if (PARENT_ROLES.has(role) /* || ORG_ADMIN_ROLES.has(role) || SUPER_ROLES.has(role) */) {
        const { members } = await listAllKidCareTeamMembers(kidId);
        for (const m of members) {
          if (m.role === 'PARENT') continue;
          push({
            userId: m.userId,
            label: m.name,
            role: 'THERAPIST',
            subtitle: m.organizationName || m.sourceLabel || kidName,
          });
        }
        if (kid?.primaryTherapistId) {
          const u = await client.models.User.get({ id: kid.primaryTherapistId }, { authMode: 'apiKey' });
          if (u.data?.id) {
            push({
              userId: u.data.id,
              label: userLabel(u.data),
              role: u.data.role,
              subtitle: `${kidName} · Primary therapist`,
            });
          }
        }
      }

      if (THERAPIST_ROLES.has(role) /* || ORG_ADMIN_ROLES.has(role) || SUPER_ROLES.has(role) */) {
        if (kid?.parentId) {
          const u = await client.models.User.get({ id: kid.parentId }, { authMode: 'apiKey' });
          if (u.data?.id) {
            push({
              userId: u.data.id,
              label: userLabel(u.data),
              role: u.data.role || 'PARENT',
              subtitle: `${kidName} · Parent`,
            });
          }
        }
        // Team parentId fallback
        try {
          const teams = await client.models.Team.list({
            filter: { kidProfileId: { eq: kidId } },
            limit: 20,
            authMode: 'apiKey',
          } as any);
          for (const team of teams.data || []) {
            const parentId = team?.parentId || team?.adminId;
            if (!parentId) continue;
            const u = await client.models.User.get({ id: parentId }, { authMode: 'apiKey' });
            if (u.data?.id) {
              push({
                userId: u.data.id,
                label: userLabel(u.data),
                role: u.data.role || 'PARENT',
                subtitle: `${kidName} · Parent`,
              });
            }
          }
        } catch {
          /* optional */
        }
      }

      return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    if (PARENT_ROLES.has(role)) {
      const kids = await listKidProfilesForParentSession();
      for (const kid of kids) {
        const { members } = await listAllKidCareTeamMembers(kid.id);
        for (const m of members) {
          if (m.role === 'PARENT') continue;
          push({
            userId: m.userId,
            label: m.name,
            role: 'THERAPIST',
            subtitle: m.organizationName || m.sourceLabel || kid.name || null,
          });
        }
        try {
          const full = await client.models.KidProfile.get({ id: kid.id }, { authMode: 'apiKey' });
          const primaryId = full.data?.primaryTherapistId;
          if (primaryId) {
            const u = await client.models.User.get({ id: primaryId }, { authMode: 'apiKey' });
            if (u.data?.id) {
              push({
                userId: u.data.id,
                label: userLabel(u.data),
                role: u.data.role,
                subtitle: kid.name || 'Primary therapist',
              });
            }
          }
        } catch {
          /* optional */
        }
      }
      return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    if (THERAPIST_ROLES.has(role)) {
      let nextToken: string | undefined;
      do {
        const resp = await client.models.KidProfile.list({
          filter: { primaryTherapistId: { eq: params.senderUserId } },
          limit: 100,
          nextToken,
          authMode: 'apiKey',
        } as any);
        for (const kid of resp.data || []) {
          if (!kid?.parentId) continue;
          try {
            const u = await client.models.User.get({ id: kid.parentId }, { authMode: 'apiKey' });
            if (u.data?.id) {
              push({
                userId: u.data.id,
                label: userLabel(u.data),
                role: u.data.role || 'PARENT',
                subtitle: kid.name || null,
              });
            }
          } catch {
            /* optional */
          }
        }
        nextToken = resp.nextToken ?? undefined;
      } while (nextToken);

      try {
        const teams = await client.models.Team.list({
          filter: {
            or: [
              { therapistId: { eq: params.senderUserId } },
              { therapist2Id: { eq: params.senderUserId } },
            ],
          },
          limit: 100,
          authMode: 'apiKey',
        } as any);
        for (const team of teams.data || []) {
          const parentId = team?.parentId || team?.adminId;
          if (!parentId) continue;
          try {
            const u = await client.models.User.get({ id: parentId }, { authMode: 'apiKey' });
            if (u.data?.id) {
              push({
                userId: u.data.id,
                label: userLabel(u.data),
                role: u.data.role || 'PARENT',
                subtitle: 'Care team',
              });
            }
          } catch {
            /* optional */
          }
        }
      } catch {
        /* Team optional */
      }

      return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    // Org admin / super admin recipient lists disabled for now
    // if (ORG_ADMIN_ROLES.has(role) && params.organizationId) {
    //   ...
    // }
    // if (SUPER_ROLES.has(role)) {
    //   ...
    // }
  } catch (e) {
    console.warn('listRecipientOptionsForSender', e);
  }

  return [];
}

async function resolveBroadcastRecipientIds(params: {
  audience: MessageAudience;
  organizationId: string;
  excludeUserId: string;
}): Promise<string[]> {
  const users = await listUsersByOrg(params.organizationId);
  const ids: string[] = [];
  for (const u of users) {
    if (!u.id || u.id === params.excludeUserId) continue;
    const ur = normalizeRole(u.role);
    if (params.audience === 'ORG_EVERYONE') {
      ids.push(u.id);
    } else if (params.audience === 'ORG_THERAPISTS' && (THERAPIST_ROLES.has(ur) || ur === 'ORG_ADMIN')) {
      ids.push(u.id);
    } else if (params.audience === 'ORG_PARENTS' && PARENT_ROLES.has(ur)) {
      ids.push(u.id);
    }
  }
  return ids.slice(0, 200);
}

export async function listInboxMessages(
  userId: string,
  opts?: { kidProfileId?: string | null },
): Promise<AppMessage[]> {
  if (!userId) return [];
  try {
    const filter: Record<string, unknown> = opts?.kidProfileId
      ? { and: [{ toUserId: { eq: userId } }, { kidProfileId: { eq: opts.kidProfileId } }] }
      : { toUserId: { eq: userId } };
    const rows = await listMessagesGraphql(filter);
    return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch (e) {
    throw explainMissing(e);
  }
}

export async function listSentMessages(
  userId: string,
  opts?: { kidProfileId?: string | null },
): Promise<AppMessage[]> {
  if (!userId) return [];
  try {
    const filter: Record<string, unknown> = opts?.kidProfileId
      ? { and: [{ fromUserId: { eq: userId } }, { kidProfileId: { eq: opts.kidProfileId } }] }
      : { fromUserId: { eq: userId } };
    const rows = await listMessagesGraphql(filter);
    return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch (e) {
    throw explainMissing(e);
  }
}

export async function listKidThreadMessages(
  userId: string,
  kidProfileId: string,
): Promise<AppMessage[]> {
  const [inbox, sent] = await Promise.all([
    listInboxMessages(userId, { kidProfileId }),
    listSentMessages(userId, { kidProfileId }),
  ]);
  const byId = new Map<string, AppMessage>();
  for (const m of [...inbox, ...sent]) byId.set(m.id, m);
  return Array.from(byId.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Cheap equality check so live polls don't thrash React state. */
export function messagesFingerprint(messages: AppMessage[]): string {
  return messages.map((m) => `${m.id}:${m.readAt || ''}`).join('|');
}

/**
 * Live updates while Messages is open: Amplify onCreate if available, plus silent poll.
 * Call unsubscribe on unmount.
 */
export function subscribeKidMessagesLive(params: {
  userId: string;
  kidProfileId: string;
  onMessages: (messages: AppMessage[]) => void;
  intervalMs?: number;
}): () => void {
  let stopped = false;
  let lastFp = '';
  const intervalMs = params.intervalMs ?? 2000;

  const pull = async () => {
    if (stopped) return;
    try {
      const rows = await listKidThreadMessages(params.userId, params.kidProfileId);
      if (stopped) return;
      const fp = messagesFingerprint(rows);
      if (fp !== lastFp) {
        lastFp = fp;
        params.onMessages(rows);
      }
    } catch {
      /* keep polling */
    }
  };

  void pull();
  const timer = window.setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void pull();
  }, intervalMs);

  const onFocus = () => {
    void pull();
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onFocus);

  // Best-effort AppSync model subscription (works after Message is in amplify_outputs)
  let amplifyUnsub: { unsubscribe: () => void } | null = null;
  try {
    const model = (client.models as any)?.Message;
    if (model?.onCreate) {
      const sub = model.onCreate({
        filter: { kidProfileId: { eq: params.kidProfileId } },
      }).subscribe({
        next: () => {
          void pull();
        },
        error: () => {
          /* fall back to poll only */
        },
      });
      amplifyUnsub = sub;
    }
  } catch {
    /* poll only */
  }

  return () => {
    stopped = true;
    window.clearInterval(timer);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onFocus);
    try {
      amplifyUnsub?.unsubscribe();
    } catch {
      /* ignore */
    }
  };
}

export type ChatConversationPreview = {
  peerUserId: string;
  label: string;
  subtitle?: string | null;
  lastMessage?: string | null;
  lastAt?: string | null;
  unreadCount: number;
};

export type CrossKidConversationPreview = {
  kidProfileId: string;
  kidName: string;
  peerUserId: string;
  peerLabel: string;
  lastMessage: string | null;
  lastAt: string | null;
  unreadCount: number;
  hasSent: boolean;
  hasReceived: boolean;
};

/** Group all messages into per-child, per-peer conversation rows for the org dashboard hub. */
export function buildCrossKidConversationPreviews(params: {
  myUserId: string;
  messages: AppMessage[];
  kidNames: Record<string, string>;
  peerLabels: Record<string, string>;
}): CrossKidConversationPreview[] {
  const buckets = new Map<
    string,
    {
      kidProfileId: string;
      peerUserId: string;
      messages: AppMessage[];
    }
  >();

  for (const message of params.messages) {
    const peerUserId =
      message.fromUserId === params.myUserId ? message.toUserId : message.fromUserId;
    if (!peerUserId || !message.kidProfileId) continue;
    const key = `${message.kidProfileId}:${peerUserId}`;
    const existing = buckets.get(key);
    if (existing) {
      existing.messages.push(message);
    } else {
      buckets.set(key, {
        kidProfileId: message.kidProfileId,
        peerUserId,
        messages: [message],
      });
    }
  }

  const previews: CrossKidConversationPreview[] = [];
  for (const bucket of buckets.values()) {
    const sorted = [...bucket.messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const last = sorted[sorted.length - 1];
    previews.push({
      kidProfileId: bucket.kidProfileId,
      kidName: params.kidNames[bucket.kidProfileId] || 'Child',
      peerUserId: bucket.peerUserId,
      peerLabel: params.peerLabels[bucket.peerUserId] || 'User',
      lastMessage: last?.body || null,
      lastAt: last?.createdAt || null,
      unreadCount: sorted.filter((m) => m.toUserId === params.myUserId && !m.readAt).length,
      hasSent: sorted.some((m) => m.fromUserId === params.myUserId),
      hasReceived: sorted.some((m) => m.toUserId === params.myUserId),
    });
  }

  return previews.sort((a, b) => {
    const at = a.lastAt || '';
    const bt = b.lastAt || '';
    if (at && bt) return bt.localeCompare(at);
    if (at) return -1;
    if (bt) return 1;
    return a.peerLabel.localeCompare(b.peerLabel);
  });
}

/** Group kid-scoped messages into WhatsApp-style conversation previews. */
export function buildConversationPreviews(params: {
  myUserId: string;
  messages: AppMessage[];
  contacts: MessageRecipientOption[];
}): ChatConversationPreview[] {
  const contactById = new Map(params.contacts.map((c) => [c.userId, c]));
  const peerIds = new Set<string>(params.contacts.map((c) => c.userId));

  for (const m of params.messages) {
    const peer = m.fromUserId === params.myUserId ? m.toUserId : m.fromUserId;
    if (peer) peerIds.add(peer);
  }

  const previews: ChatConversationPreview[] = [];
  for (const peerUserId of peerIds) {
    const thread = params.messages.filter(
      (m) =>
        (m.fromUserId === params.myUserId && m.toUserId === peerUserId) ||
        (m.fromUserId === peerUserId && m.toUserId === params.myUserId),
    );
    const last = thread.length ? thread[thread.length - 1] : null;
    const contact = contactById.get(peerUserId);
    previews.push({
      peerUserId,
      label: contact?.label || 'User',
      subtitle: contact?.subtitle || contact?.role || null,
      lastMessage: last?.body || null,
      lastAt: last?.createdAt || null,
      unreadCount: thread.filter((m) => m.toUserId === params.myUserId && !m.readAt).length,
    });
  }

  return previews.sort((a, b) => {
    const at = a.lastAt || '';
    const bt = b.lastAt || '';
    if (at && bt) return bt.localeCompare(at);
    if (at) return -1;
    if (bt) return 1;
    return a.label.localeCompare(b.label);
  });
}

/** Fired when inbox unread may have changed (e.g. after mark-read). */
export const MESSAGING_UNREAD_CHANGED_EVENT = 'tinywins:messaging-unread-changed';

export function notifyMessagingUnreadChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(MESSAGING_UNREAD_CHANGED_EVENT));
}

/** Unread chat count for Messages badge — one badge unit per unopened conversation (not per message). */
export async function countUnreadInboxMessages(userId: string): Promise<number> {
  if (!userId) return 0;
  try {
    const [inbox, sent] = await Promise.all([
      listInboxMessages(userId),
      listSentMessages(userId),
    ]);
    const byId = new Map<string, AppMessage>();
    for (const message of [...inbox, ...sent]) byId.set(message.id, message);
    const allMessages = Array.from(byId.values());

    const peerIds = new Set<string>();
    for (const message of allMessages) {
      const peer =
        message.fromUserId === userId ? message.toUserId : message.fromUserId;
      if (peer) peerIds.add(peer);
    }
    const peerRoles = await resolveUserRolesById([...peerIds]);

    const isUnopenedForMe = (message: AppMessage) =>
      message.toUserId === userId &&
      !message.readAt &&
      message.fromUserId !== userId &&
      !parseGroupCreateMeta(message.body);

    let unreadChats = 0;

    // Direct team chats: 1 chat with any unopened msg → count 1
    const teamMessages = filterMessagesForOrgTeamHub(allMessages, userId, peerRoles);
    const unreadPeerIds = new Set<string>();
    for (const message of teamMessages) {
      if (!isUnopenedForMe(message)) continue;
      if (message.fromUserId) unreadPeerIds.add(message.fromUserId);
    }
    unreadChats += unreadPeerIds.size;

    // Group / broadcast: each channel with unread → +1
    for (const group of listTherapistChatGroupsFromMessages(userId, allMessages)) {
      if (group.unreadCount > 0) unreadChats += 1;
    }

    // Platform announcements thread
    const platform = buildPlatformAnnouncementPreview(userId, allMessages, peerRoles);
    if (platform && platform.unreadCount > 0) unreadChats += 1;

    return unreadChats;
  } catch {
    return 0;
  }
}

export async function markMessageRead(messageId: string): Promise<void> {
  try {
    await updateMessageGraphql(messageId, new Date().toISOString());
    notifyMessagingUnreadChanged();
  } catch (e) {
    throw explainMissing(e);
  }
}

export async function sendMessage(params: {
  fromUserId: string;
  fromRole?: string | null;
  body: string;
  toUserId?: string | null;
  audience?: MessageAudience;
  organizationId?: string | null;
  kidProfileId?: string | null;
  threadId?: string | null;
}): Promise<{ created: number }> {
  const body = params.body.trim();
  if (!body) throw new Error('Message cannot be empty.');
  if (!params.fromUserId) throw new Error('Sender is required.');

  const audience: MessageAudience = params.audience || 'USER';
  const createdAt = new Date().toISOString();
  const threadId = params.threadId || undefined;

  let recipientIds: string[] = [];
  if (audience === 'USER') {
    if (!params.toUserId) throw new Error('Pick a recipient.');
    recipientIds = [params.toUserId];
  } else {
    if (!params.organizationId) throw new Error('Organization is required for broadcast.');
    recipientIds = await resolveBroadcastRecipientIds({
      audience,
      organizationId: params.organizationId,
      excludeUserId: params.fromUserId,
    });
    if (recipientIds.length === 0) throw new Error('No recipients found for this audience.');
  }

  // Role gate (light client-side check)
  const role = normalizeRole(params.fromRole);
  if (SUPER_ROLES.has(role)) {
    throw new Error('Super-admin messaging is turned off for now.');
  }
  const isOrgTeamDm =
    isOrgTeamMessagingRole(role) && !params.kidProfileId && audience === 'USER';
  if (isOrgTeamDm) {
    if (!params.organizationId) {
      throw new Error('Organization is required to message a teammate.');
    }
    if (!params.toUserId) throw new Error('Pick a teammate to message.');
    const recipient = await client.models.User.get({ id: params.toUserId }, { authMode: 'apiKey' });
    const recipientRole = normalizeRole(recipient.data?.role);
    const sameOrg = (recipient.data?.organizationId || '') === params.organizationId;
    if (!sameOrg || !isOrgTeamMessagingRole(recipientRole)) {
      throw new Error('You can only message therapists or org staff in your organization.');
    }
  } else if (ORG_ADMIN_ROLES.has(role)) {
    throw new Error('Organization admin messaging outside team DMs is turned off for now.');
  } else if ((PARENT_ROLES.has(role) || THERAPIST_ROLES.has(role)) && !params.kidProfileId) {
    throw new Error('Open messages from a child’s profile to send.');
  }
  if (PARENT_ROLES.has(role) && audience !== 'USER') {
    throw new Error('Parents can only message one therapist at a time.');
  }
  if (THERAPIST_ROLES.has(role) && audience !== 'USER') {
    throw new Error('Therapists can only message one person at a time.');
  }

  const messagesPath = params.kidProfileId
    ? `/messages/${encodeURIComponent(params.kidProfileId)}`
    : '/org-admin-dashboard?section=messages';

  try {
    let created = 0;
    const rootId = uuidv4();
    for (const toUserId of recipientIds) {
      const id = audience === 'USER' && recipientIds.length === 1 ? rootId : uuidv4();
      await createMessageGraphql({
        id,
        fromUserId: params.fromUserId,
        toUserId,
        organizationId: params.organizationId || undefined,
        kidProfileId: params.kidProfileId || undefined,
        body,
        audience,
        threadId: threadId || rootId,
        createdAt,
      });
      created += 1;

      try {
        const senderName = await resolveUserLabel(params.fromUserId);
        const preview = body.length > 100 ? `${body.slice(0, 97)}…` : body;
        const withParam = encodeURIComponent(params.fromUserId);
        const chatUrl = params.kidProfileId
          ? `${messagesPath}?with=${withParam}`
          : `${messagesPath}&with=${withParam}`;
        await createNotification({
          toUserId,
          fromUserId: params.fromUserId,
          organizationId: params.organizationId,
          kidProfileId: params.kidProfileId,
          notificationType: 'MESSAGE_RECEIVED',
          title: `Message from ${senderName}`,
          body: preview,
          payload: {
            kind: 'USER_MESSAGE',
            orgTeam: isOrgTeamDm || undefined,
            messageId: id,
            threadId: threadId || rootId,
            kidProfileId: params.kidProfileId || null,
            fromUserId: params.fromUserId,
          },
          actionUrl: chatUrl,
        });
      } catch (notifErr) {
        console.warn(
          '[messaging] notification create failed:',
          notifErr instanceof Error ? notifErr.message : notifErr,
        );
      }
    }
    return { created };
  } catch (e) {
    throw explainMissing(e);
  }
}

export async function resolveUserLabel(userId: string): Promise<string> {
  try {
    const resp = await client.models.User.get({ id: userId }, { authMode: 'apiKey' });
    if (!resp.data) return 'User';
    return userLabel(resp.data);
  } catch {
    return 'User';
  }
}

export type OrgAdminRecipient = {
  userId: string;
  label: string;
  organizationId?: string | null;
  organizationName?: string | null;
};

/** All ORG_ADMIN users across organizations (apiKey User list). */
export async function listAllOrgAdminUsers(): Promise<OrgAdminRecipient[]> {
  const byId = new Map<string, OrgAdminRecipient>();
  let nextToken: string | undefined;
  do {
    const resp = await client.models.User.list({
      filter: { role: { eq: 'ORG_ADMIN' } },
      limit: 200,
      nextToken,
      authMode: 'apiKey',
    } as any);
    for (const row of resp.data || []) {
      if (!row?.id) continue;
      byId.set(row.id, {
        userId: row.id,
        label: userLabel(row as any),
        organizationId: (row as any).organizationId ?? null,
        organizationName: null,
      });
    }
    nextToken = resp.nextToken ?? undefined;
  } while (nextToken);

  const orgIds = [
    ...new Set(
      Array.from(byId.values())
        .map((r) => r.organizationId)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  const orgNames: Record<string, string> = {};
  await Promise.all(
    orgIds.map(async (orgId) => {
      try {
        const org = await client.models.Organization.get({ id: orgId }, { authMode: 'apiKey' });
        orgNames[orgId] = (org.data?.name || '').trim() || 'Organization';
      } catch {
        orgNames[orgId] = 'Organization';
      }
    }),
  );

  return Array.from(byId.values())
    .map((r) => ({
      ...r,
      organizationName: r.organizationId ? orgNames[r.organizationId] || 'Organization' : 'No organization',
    }))
    .sort((a, b) => {
      const orgCmp = (a.organizationName || '').localeCompare(b.organizationName || '');
      if (orgCmp !== 0) return orgCmp;
      return a.label.localeCompare(b.label);
    });
}

export type SuperAdminBroadcastPreview = {
  threadId: string;
  body: string;
  createdAt: string;
  recipientCount: number;
};

/**
 * SUPER_ADMIN → fan-out announcement to ORG_ADMIN users (one Message copy each, shared threadId).
 * Pass `recipientUserIds` to target a subset; omit to send to every org admin.
 */
export async function broadcastToAllOrgAdmins(params: {
  fromUserId: string;
  fromRole?: string | null;
  body: string;
  /** If set, only these user IDs receive the broadcast (must be ORG_ADMIN). */
  recipientUserIds?: string[];
}): Promise<{ created: number; threadId: string; recipientCount: number }> {
  if (!isSuperAdminRole(params.fromRole)) {
    throw new Error('Only super admins can broadcast to all org admins.');
  }
  const text = params.body.trim();
  if (!text) throw new Error('Message cannot be empty.');

  const allAdmins = await listAllOrgAdminUsers();
  const allowed = new Set(
    allAdmins.map((r) => r.userId).filter((id) => id && id !== params.fromUserId),
  );

  let recipientIds: string[];
  if (params.recipientUserIds && params.recipientUserIds.length > 0) {
    recipientIds = [
      ...new Set(params.recipientUserIds.map(String).filter((id) => allowed.has(id))),
    ];
    if (recipientIds.length === 0) {
      throw new Error('Select at least one organization admin.');
    }
  } else {
    recipientIds = [...allowed];
    if (recipientIds.length === 0) {
      throw new Error('No organization admins found to broadcast to.');
    }
  }

  const threadId = uuidv4();
  const createdAt = new Date().toISOString();
  let created = 0;
  const preview = text.length > 100 ? `${text.slice(0, 97)}…` : text;

  for (const toUserId of recipientIds) {
    const id = uuidv4();
    await createMessageGraphql({
      id,
      fromUserId: params.fromUserId,
      toUserId,
      body: text,
      audience: 'USER',
      threadId,
      createdAt,
    });
    created += 1;
    try {
      await createNotification({
        toUserId,
        fromUserId: params.fromUserId,
        notificationType: 'MESSAGE_RECEIVED',
        title: 'Official announcement',
        body: preview,
        payload: {
          kind: 'USER_MESSAGE',
          platformBroadcast: true,
          threadId,
          fromUserId: params.fromUserId,
        },
        actionUrl: '/org-admin-dashboard?section=messages&announcement=platform',
      });
    } catch {
      /* non-blocking */
    }
  }

  return { created, threadId, recipientCount: recipientIds.length };
}

/** Collapse super-admin fan-out sends into one row per broadcast. */
export function listSuperAdminBroadcastPreviews(
  myUserId: string,
  sentMessages: AppMessage[],
): SuperAdminBroadcastPreview[] {
  const byThread = new Map<string, AppMessage[]>();
  for (const m of sentMessages) {
    if (m.fromUserId !== myUserId || m.kidProfileId) continue;
    // Platform broadcasts do not set organizationId (org team DMs do).
    if (m.organizationId) continue;
    if (!m.threadId) continue;
    const rows = byThread.get(m.threadId) || [];
    rows.push(m);
    byThread.set(m.threadId, rows);
  }

  const out: SuperAdminBroadcastPreview[] = [];
  for (const [threadId, rows] of byThread) {
    // Platform broadcasts fan out to 2+ org admins (or 1 if only one exists).
    if (rows.length < 1) continue;
    const sorted = [...rows].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const first = sorted[0];
    out.push({
      threadId,
      body: first.body,
      createdAt: first.createdAt,
      recipientCount: new Set(rows.map((r) => r.toUserId).filter(Boolean)).size,
    });
  }

  return out.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}
